import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EnvironmentSelector } from '@/components/shared/environment-selector';
import { useDashboardStore } from '@/features/dashboard';
import { useAuthStore } from '@/features/auth';
import { UserApiService } from '@/services/api/users/user-api';
import { NotificationsApiService, type NotificationItem } from '@/services/api/notifications/notifications-api';
import { ROUTE_PATHS } from '@/config/routes.config';
import { formatRoleName } from '@/lib/utils';
import { formatGMT7 } from '@/lib/utils/timezone.util';
import { Menu, Bell, User, Moon, Sun, LogOut, Check, UserCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const { toggleSidebar, setSidebarOpen, sidebarOpen, theme, setTheme } = useDashboardStore();
  const { user, logout, updateUser } = useAuthStore();
  const navigate = useNavigate();

  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<NotificationItem[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [countRes, listRes] = await Promise.all([
        NotificationsApiService.getUnreadCount(),
        NotificationsApiService.getNotifications(1, 5),
      ]);
      setUnreadCount(countRes.count);
      setRecentNotifications(listRes.items);
    } catch {
      // Non-admins may not have access; keep silent
    }
  }, [user]);

  useEffect(() => {
    void loadNotifications();
    const interval = setInterval(() => {
      void loadNotifications();
    }, 60_000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleToggleTheme = async () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    const apiTheme = next === 'dark' ? 'DARK' : 'LIGHT';
    try {
      await UserApiService.updateProfile({ theme: apiTheme });
      updateUser({ theme: apiTheme });
    } catch {
      // Revert on failure
      setTheme(theme);
      toast.error('Failed to save theme preference');
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await NotificationsApiService.markRead(id);
      await loadNotifications();
    } catch {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    setNotifLoading(true);
    try {
      await NotificationsApiService.markAllRead();
      await loadNotifications();
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setNotifLoading(false);
    }
  };

  const handleMenuClick = () => {
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      toggleSidebar();
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 border-b border-border/80 bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/75 ${className}`}>
      <div className="flex h-14 sm:h-16 items-center px-2 sm:px-4">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleMenuClick}
            className="h-8 w-8"
            aria-label="Toggle menu"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
            <img
              src={`${import.meta.env.BASE_URL}logo.png`}
              alt="UNICEF"
              className="h-7 w-7 sm:h-8 sm:w-8 shrink-0 rounded-md object-contain"
            />
            <h1 className="text-base sm:text-lg md:text-xl font-semibold">
              <span className="sm:hidden">UNICEF Salesforce</span>
              <span className="hidden sm:inline">UNICEF Salesforce Middleware</span>
            </h1>
            <Badge variant="outline" className="text-xs hidden sm:inline-flex border-primary/30 text-primary">
              v2.1.0
            </Badge>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-end space-x-1 sm:space-x-2 md:space-x-4">
          <div className="hidden md:block">
            <EnvironmentSelector />
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleTheme}
            className="h-8 w-8"
            aria-label="Toggle theme"
          >
            {theme === 'light' ? (
              <Moon className="h-4 w-4" />
            ) : (
              <Sun className="h-4 w-4" />
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hidden sm:inline-flex relative"
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-2 py-1.5">
                <p className="text-sm font-medium">Notifications</p>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={notifLoading}
                    onClick={handleMarkAllRead}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Mark all read
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />
              {recentNotifications.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No notifications
                </div>
              ) : (
                recentNotifications.map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    className="flex flex-col items-start gap-1 cursor-pointer py-2"
                    onClick={() => {
                      if (!n.readAt) void handleMarkRead(n.id);
                      if (n.link) {
                        navigate(n.link.startsWith('/') ? n.link.slice(1) : n.link);
                      } else {
                        navigate(`/${ROUTE_PATHS.NOTIFICATIONS}`);
                      }
                    }}
                  >
                    <div className="flex w-full items-start justify-between gap-2">
                      <span className={`text-sm ${!n.readAt ? 'font-medium' : ''}`}>{n.title}</span>
                      {!n.readAt && (
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatGMT7(n.createdAt)}
                    </span>
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={`/${ROUTE_PATHS.NOTIFICATIONS}`} className="w-full text-center text-sm">
                  View all notifications
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="User menu">
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="flex items-center justify-start gap-2 p-2">
                <div className="flex flex-col space-y-1 leading-none">
                  <p className="font-medium">{user?.name || 'User'}</p>
                  <p className="w-[200px] truncate text-sm text-muted-foreground">
                    {user?.email || 'user@example.com'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatRoleName(user?.role)}
                  </p>
                </div>
              </div>
              <DropdownMenuSeparator />
              <div className="md:hidden px-2 py-1 w-full">
                <EnvironmentSelector />
              </div>
              <div className="md:hidden">
                <DropdownMenuSeparator />
              </div>
              <DropdownMenuItem asChild>
                <Link to={`/${ROUTE_PATHS.PROFILE}`}>
                  <UserCircle className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
